import { useState } from "react";
import { taskService } from "@miplatform/api";
import {
  ATTEMPT_STATUSES,
  ENTITY_TYPES,
  HTTP_STATUSES,
  TIME_UNITS,
} from "@miplatform/constants";
import { useComponentState, useDebounce, useRouter } from "@miplatform/hooks";
import {
  CalendarType,
  IAttemptListItemResponseForWorkOrder,
  IAttemptListResponse,
  IAttemptTrayConfig,
  ICalendarConfig,
  IDateRangeISO,
  IOrderTemplateResponse,
} from "@miplatform/types";
import {
  bugsnag,
  buildActivityDateFilter,
  buildSearchQuery,
  endOf,
  getDate,
  getGUIDArray,
  isEmpty,
  isSameOrAfter,
  isSameOrBefore,
  startOf,
} from "@miplatform/utils";
import { ActivityDateRange } from "@mibuildweb/components";
import { useCalendarContext } from "../../providers";
import { FOLLOW_UP_TRAY_FILTERS } from "../Config/Calendars/ConfigCalendarSetList.constants";

export const useNhcBoardViewModel = () => {
  const stateManager = useComponentState();
  const isLoading = stateManager.isBusy();
  const { push } = useRouter();
  const { date, dateRange, calendarSet, calendarConfig } = useCalendarContext();

  const [orders, setOrders] = useState<
    Array<{
      name: string;
      data: IAttemptListResponse[];
      trayBuckets: IAttemptTrayConfig[];
    }>
  >([]);
  const [orderTemplates, setOrderTemplates] = useState<
    Record<string, IOrderTemplateResponse>
  >({});
  const [orderReasons, setOrderReasons] = useState<{
    holdReasons: { value: string; label: string }[];
    rescheduleReasons: { value: string; label: string }[];
  }>({
    holdReasons: [],
    rescheduleReasons: [],
  });
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 500);

  const [selectedOrder, setSelectedOrder] = useState<
    IAttemptListResponse | IAttemptListItemResponseForWorkOrder | undefined
  >(undefined);
  const [orderDetails, setOrderDetails] = useState<any>({});
  const [isCreateNewOrderDrawerOpen, setIsCreateNewOrderDrawerOpen] =
    useState(false);

  const handleSearch = (value: string) => {
    setSearch(value);
  };

  const handleTabChange = (tabId: string) => {
    setSearch("");
    push(`/scheduling/${calendarSet?.config?.id}/${tabId}`);
  };

  const getBucketConfig = () => {
    if (!calendarConfig?.trays) return [];
    return calendarConfig.trays;
  };

  const shouldShowBucket = () => {
    return !!calendarConfig?.trays?.length;
  };

  const getOrdersForCalendar = async (calendar: ICalendarConfig) => {
    const orderTemplateIds =
      calendar.workTemplateBases?.map((template) => template.id) ?? [];
    return stateManager.run(async () => {
      const filters: any = {};

      if (
        dateRange.startDate &&
        calendar.calendarType !== CalendarType.HoldsList
      ) {
        const startDay = startOf(TIME_UNITS.DAY, dateRange.startDate);
        const endDay = endOf(
          TIME_UNITS.DAY,
          dateRange.endDate ?? dateRange.startDate
        );

        filters.displayDate = {
          ge: startDay.toDate(),
          le: endDay.toDate(),
        };
      }

      if (calendar.calendarType === CalendarType.Scheduler) {
        filters.displayDate = {
          ge: getDate(date).startOf("day").toDate(),
          le: getDate(date).endOf("day").toDate(),
        };
      }

      if (search) {
        const searchQuery = buildSearchQuery(search);

        filters.or = searchQuery;
      }

      if (calendar?.filter) {
        Object.assign(filters, calendar.filter);
      }

      if (!isEmpty(orderTemplateIds)) {
        if (calendarSet?.config?.eventType === ENTITY_TYPES.WORK_ORDER) {
          filters.assignment = {
            workOrder: {
              workOrderTemplateId: {
                in: orderTemplateIds ?? [],
              },
            },
          };
        } else {
          filters.assignment = {
            order: {
              orderTemplateId: {
                in: orderTemplateIds ?? [],
              },
            },
          };
        }
      }

      if (calendar?.calendarType === CalendarType.HoldsList) {
        if (calendarSet?.config?.eventType === ENTITY_TYPES.WORK_ORDER) {
          Object.assign(filters, {
            ...calendarConfig?.filter,
            attemptStatus: { eq: ATTEMPT_STATUSES.HOLD },
            isOrderFollowupDue: false,
            assignment: {
              workOrder: {
                holdCalendarId: {
                  type: "guid",
                  value: calendar.id,
                },
                ...(orderTemplateIds?.length > 0 && {
                  workOrderTemplateId: {
                    in: getGUIDArray(orderTemplateIds),
                  },
                }),
              },
            },
          });
        } else {
          Object.assign(filters, {
            ...calendarConfig?.filter,
            attemptStatus: { eq: ATTEMPT_STATUSES.HOLD },
            isOrderFollowupDue: false,
            assignment: {
              order: {
                holdCalendarId: {
                  type: "guid",
                  value: calendar.id,
                },
                ...(orderTemplateIds?.length > 0 && {
                  orderTemplateId: {
                    in: getGUIDArray(orderTemplateIds),
                  },
                }),
              },
            },
          });
        }
      }

      const response = await taskService.attempts.getAll({
        filters,
        params: {
          includeWorkOrders: true,
        },
      });

      if (response.status === HTTP_STATUSES.OK) {
        return response.data;
      }

      return [];
    });
  };

  // Unified tray data fetching - used by both Kanban buckets and HoldsList
  const getTrayDataForCalendar = async (calendar: ICalendarConfig) => {
    if (!calendarSet?.config?.trays?.length) {
      return { trayBuckets: [], allTrayData: [] };
    }

    const attachedTrayCalendarMap = Object.values(calendarSet.calendars).reduce(
      (acc: Record<string, string[]>, calendar) => {
        if (calendar.config.attachedTray) {
          if (!acc[calendar.config.attachedTray.id]) {
            acc[calendar.config.attachedTray.id] = [];
          }
          acc[calendar.config.attachedTray.id].push(calendar.config.id);
        }
        return acc;
      },
      {}
    );

    const orderTemplateIds =
      calendar.workTemplateBases?.map((template) => template.id) ?? [];

    const trayBuckets: IAttemptTrayConfig[] = [];
    const allTrayData: IAttemptListResponse[] = [];

    const trayPromises = calendarSet.config.trays.map(
      async (tray: IAttemptTrayConfig) => {
        const filterStringParts = [];
        if (tray?.filter) {
          filterStringParts.push(`(${tray?.filter})`);
        }

        if (calendarSet.config.eventType && orderTemplateIds) {
          filterStringParts.push(
            `assignment/${calendarSet.config.eventType}/${calendarSet.config.eventType}TemplateId in (${orderTemplateIds})`
          );
        }

        // TODO : yuck, but elsewhere we are matching on filter to determine type like in the "Tray Type" dropdown in calendar configs
        // this will filter all Follow-Up trays to limit them to the attached Hold Reason Tabs/Calendar Ids, to prevent
        // trays that have no attached calendars (hold reasons) from finding orders from ALL calendars instead of none.
        if (tray?.filter === FOLLOW_UP_TRAY_FILTERS) {
          const trayAttachedCalendarIds =
            attachedTrayCalendarMap[tray.id] ?? [];
          if (trayAttachedCalendarIds.length < 1) {
            // a follow up tray with no attached calendars
            // this tray will always be empty, so skip the API call
            return {
              tray,
              data: [],
            };
          }
          filterStringParts.push(
            `assignment/${
              calendarSet.config.eventType
            }/holdCalendarId in (${trayAttachedCalendarIds.join(",")})`
          );
        }

        const filterString = filterStringParts.join(" and ");

        try {
          const response = await taskService.attempts.getAll({
            filters: filterString,
            params: {
              includeWorkOrders: true,
            },
            ...(tray.sort &&
              typeof tray.sort === "string" && { sort: tray.sort }),
          });

          if (response.status === HTTP_STATUSES.OK) {
            return {
              tray,
              data: response.data.map((attempt: IAttemptListResponse) => {
                return {
                  ...attempt,
                  isRequestedInDateRange:
                    attempt.assignment?.order?.requestedStartDate &&
                    isSameOrAfter(
                      attempt.assignment?.order?.requestedStartDate,
                      dateRange.startDate
                    ) &&
                    isSameOrBefore(
                      attempt.assignment?.order?.requestedStartDate,
                      dateRange.endDate ?? dateRange.startDate
                    ),
                };
              }),
            };
          }
        } catch (trayError) {
          bugsnag.setContext("useNhcBoardViewModel::getTrayDataForCalendar");
          bugsnag.notify(
            trayError instanceof Error
              ? trayError
              : new Error(String(trayError))
          );
        }

        return {
          tray,
          data: [],
        };
      }
    );

    try {
      const trayResults = await Promise.all(trayPromises);

      trayResults.forEach(({ tray, data }) => {
        allTrayData.push(...data);

        trayBuckets.push({
          id: tray.id,
          isActive: tray.isActive,
          isDefault: tray.isDefault,
          name: tray.name,
          collapsible: tray.collapsible,
          displayOrder: tray.displayOrder,
          headerColor: tray.headerColor,
          filter: tray.filter,
          headerBackgroundColor: tray.headerBackgroundColor,
          contentBackgroundColor: tray.contentBackgroundColor,
          countBackgroundColor: tray.countBackgroundColor,
          countColor: tray.countColor,
          hideWhenEmpty: tray.hideWhenEmpty,
          data,
        });
      });

      trayBuckets.sort((a, b) => a.displayOrder - b.displayOrder);
    } catch (error) {
      bugsnag.setContext("useNhcBoardViewModel::getTrayDataForCalendar");
      bugsnag.notify(error instanceof Error ? error : new Error(String(error)));
    }

    return { trayBuckets, allTrayData };
  };

  const getOrders = async (args?: { dateRange: IDateRangeISO }) => {
    return stateManager.run(async () => {
      if (!calendarConfig) {
        return;
      }

      if (calendarConfig.calendarType === CalendarType.Scheduler) {
        return;
      }

      const mainData = await getOrdersForCalendar(calendarConfig);

      // Get tray data if trays exist
      const { trayBuckets: buckets } = await getTrayDataForCalendar(
        calendarConfig
      );

      setOrders((prev) => {
        const existingIndex = prev.findIndex(
          (item) => item.name === calendarConfig.name
        );

        if (existingIndex >= 0) {
          const updated = [...prev];
          updated[existingIndex] = {
            name: calendarConfig.name,
            data: mainData,
            trayBuckets: buckets,
          };
          return updated;
        }

        return [
          ...prev,
          {
            name: calendarConfig.name,
            data: mainData,
            trayBuckets: buckets,
          },
        ];
      });
    });
  };

  const getCurrentCalendarData = () => {
    if (!calendarConfig) return [];

    const calendarData = orders.find(
      (item) => item.name === calendarConfig.name
    );
    return calendarData?.data || [];
  };

  const getCurrentTrayBuckets = () => {
    if (!calendarConfig) return [];

    const calendarData = orders.find(
      (item) => item.name === calendarConfig.name
    );
    return calendarData?.trayBuckets || [];
  };

  const getOrderTemplates = () => {
    return stateManager.run(async () => {
      const response = await taskService.orderTemplates.getAll();

      if (response.status === HTTP_STATUSES.OK) {
        const templates: Record<string, IOrderTemplateResponse> = {};
        response.data.forEach((template: IOrderTemplateResponse) => {
          templates[template.name] = template;
        });
        setOrderTemplates(templates);
      }
    });
  };

  const getOrderReasons = () => {
    return stateManager.run(async () => {
      const holdResponse = await taskService.orderReasons.getHoldReasons();

      const rescheduleResponse =
        await taskService.orderReasons.getRescheduleReasons();

      if (holdResponse.status === HTTP_STATUSES.OK) {
        const formattedData = holdResponse.data?.labels.map(
          (reason: string) => ({
            value: reason,
            label: reason,
          })
        );
        setOrderReasons((prev) => ({ ...prev, holdReasons: formattedData }));
      }

      if (rescheduleResponse.status === HTTP_STATUSES.OK) {
        const formattedData =
          rescheduleResponse.data?.map((reason: any) => ({
            value: reason.id,
            label: reason.label,
            color: reason.color,
          })) || [];
        setOrderReasons((prev) => ({
          ...prev,
          rescheduleReasons: formattedData,
        }));
      }
    });
  };

  const getOrderActivity = async (
    orderId: string,
    dateRange?: ActivityDateRange
  ) => {
    return stateManager.run(async () => {
      const response = await taskService.activities.getAll({
        params: {
          refId: `task-service/orders/${orderId}`,
        },
        filters: buildActivityDateFilter(dateRange),
      });
      return response;
    });
  };

  const getOrderById = async (orderId: string) => {
    return stateManager.run(async () => {
      try {
        const response = await taskService.order.getById({ orderId });
        if (response.status === HTTP_STATUSES.OK) {
          setOrderDetails(response.data);
          return response.data;
        }
        return null;
      } catch (error) {
        bugsnag.setContext("useNhcBoardViewModel::getOrderById");
        bugsnag.notify(
          error instanceof Error ? error : new Error(String(error))
        );
        return null;
      }
    });
  };

  const clearOrderDetails = () => {
    setOrderDetails({});
    setSelectedOrder(undefined);
  };

  const refreshOrder = () => {
    if (orderDetails?.id) {
      getOrderById(orderDetails.id);
    }
  };

  const handleAfterOrderCreation = () => {
    setIsCreateNewOrderDrawerOpen(false);
    getOrders();
  };

  return {
    date,
    dateRange,
    getOrders,
    getOrderTemplates,
    orders: getCurrentCalendarData(),
    orderReasons,
    getOrderReasons,
    orderTemplates,
    handleSearch,
    search,
    isLoading,
    setSelectedOrder,
    selectedOrder,
    orderDetails,
    getOrderById,
    clearOrderDetails,
    refreshOrder,
    getOrderActivity,
    debouncedSearch,
    handleTabChange,
    shouldShowBucket,
    getBucketConfig,
    trayBuckets: getCurrentTrayBuckets(),
    isCreateNewOrderDrawerOpen,
    setIsCreateNewOrderDrawerOpen,
    handleAfterOrderCreation,
  };
};
